import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import Pagination from '../Pagination';
import BookDisplay from '../BookDisplay';
import BookDetailsModal from "../../components/BookDetailsModal";
import CurrentlyReadingDisplay from "../../components/CurrentlyReadingDisplay";
import CurrentlyReadingDetailsModal from "../../components/CurrentlyReadingDetailsModal";
import EditShelfModal from "../../components/EditShelfModal";
import { API_BASE_URL } from "../../Variables";
import { isShelfMain } from "../../services/Utils";

import './ShelfView.css';
import back from '../../assets/images/back.svg';
import editIcon from '../../assets/images/edit-icon.svg';

const ShelfView = () => {
  // Libraries and Hooks
  const { shelfName } = useParams(); // Get genre from URL
  const navigate = useNavigate(); // Initialize navigation function
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  // State Management
  const [currentShelfName, setCurrentShelfName] = useState(shelfName);
  const [books, setBooks] = useState([]);
  const [shelfId, setShelfId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [offset, setOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);
  const [isEditable, setIsEditable] = useState(false);

  // Modal Management
  const [displayBookDetailsModal, setDisplayBookDetailsModal] = useState(false);
  const [displayCurrentlyReadingModal, setDisplayCurrentlyReadingModal] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [displayEditModal, setDisplayEditModal] = useState(false);

  // Constants
  const limit = 20; // Number of books per page
  const currentPage = Math.floor(offset / limit) + 1; // Calculate current page

  /**
   * Handle back button click
   */
  const handleBackClick = () => {
    navigate(-1);
  };

  /**
   * Handle sort change
   * @param {*} event 
   */
  const handleSortChange = (event) => {
    setSort(event.target.value);
    setOffset(0);
  }

  /**
   * Handle book click
   * Opens the book details modal
   * @param {*} bookId 
   */
  const handleBookClick = (bookId) => {
    setSelectedBookId(bookId);
    setDisplayBookDetailsModal(true);
  };

  /**
   * Handles opening and closing currently reading details modal
   * @param {*} bookId 
   */
  const handleCurrentlyReadingClick = (bookId) => {
    setSelectedBookId(bookId);
    setDisplayCurrentlyReadingModal(true);
  };

  /**
   * Handle edit click
   */
  const handleEditClick = () => {
    setDisplayEditModal(true);
  };

  /**
   * Update shelf name
   * @param {*} newName - New shelf name
   */
  const updateShelfName = (newName) => {
    setCurrentShelfName(newName);
    navigate(`/booknook/${newName}`);
  };

  /**
   * Refresh shelf
   */
  const refreshShelf = () => {
    setOffset(0);
    fetchBooks();
  };

  /**
   * Fetch books based on the shelf name
   */
  const fetchBooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/books/shelf-books`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { sort, offset, limit, shelfName: currentShelfName, user_id: userId },
      });

      const { books, totalBooks, shelf_id, shelf_type_id} = response.data;
      setTotalPages(Math.ceil(totalBooks / limit));
      setBooks(books);
      setShelfId(shelf_id);
      setIsEditable(!isShelfMain({ shelfId: shelf_type_id }) && (parseInt(userId) === currentUser.id || !userId));
    } catch (error) {
      setError('An error occurred while fetching books. ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [currentShelfName, offset, limit, sort, token, userId, currentUser.id]);

  // Fetch books dynamically based on the genre
  useEffect(() => {
    fetchBooks();
  }, [currentShelfName, offset, limit, sort, token, fetchBooks]);

  return (
    <div className="list-view">
      <div className="list-preview-header">
        <img src={back} alt="Back" className="back-button" onClick={handleBackClick} />
        <h2>{shelfName}</h2>
        {isEditable && <img src={editIcon} className="edit-icon" alt="Edit pen" onClick={handleEditClick}/>}
        
        <select value={sort} onChange={handleSortChange}>
          <option value="">Sort by</option>
          <option value="date_updated-DESC">Newest</option>
          <option value="date_updated-ASC">Oldest</option>
          <option value="title-ASC">Title (A-Z)</option>
          <option value="title-DESC">Title (Z-A)</option>
        </select>
      </div>

      <div className="list-view-display">
        {loading ? (
          <p className="loading">Loading...</p>
        ) : error ? (
          <p className="error">
            {error}
          </p>
        ) : books && books.length > 0 ? (
          shelfName === 'Currently Reading' ? (
            books.map((book, index) => (
              <CurrentlyReadingDisplay key={index} book={book} onBookClick={handleCurrentlyReadingClick} />
            ))
          ) : (
            books.map((book, index) => (
              <BookDisplay key={index} book={book} onBookClick={handleBookClick} />
            ))
          )
        ) : (
          <p className="no-books">No books available in this shelf.</p>
        )}
      </div>

      {totalPages > 0 && (
        <Pagination totalPages={totalPages} setOffset={setOffset} limit={limit} currentPage={currentPage} />
      )}

      {/* Book Details Modal */}
      {displayBookDetailsModal && (
        <BookDetailsModal
          setDisplayBookDetailsModal={setDisplayBookDetailsModal}
          bookId={selectedBookId}
          refreshShelves={refreshShelf}
        />
      )}

      {/* Currently Reading Details Modal */}
      {displayCurrentlyReadingModal && (
        <CurrentlyReadingDetailsModal
          setDisplayCurrentlyReadingModal={setDisplayCurrentlyReadingModal}
          bookId={selectedBookId}
          refreshShelves={refreshShelf}
        />
      )}

      {/* Edit Shelf Modal */}
      {displayEditModal && (
        <EditShelfModal
          shelfId={shelfId}
          shelfName={shelfName}
          onClose={setDisplayEditModal}
          onNameChange={updateShelfName}
        />
      )}
    </div>
  );
};

export default ShelfView;
